import { Info } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription } from "@/components/ui/alert"

/**
 * OnboardingDefaultCharterBanner — shown above the charter body when the
 * inviting brand has not authored a custom crew charter
 * (`charter.is_default === true`).
 *
 * **CTPO ship-it lock (2026-05-01):**
 *
 * The banner copy is **NEUTRAL** — it MUST NOT mention the brand by name
 * or imply that the brand "hasn't published" a charter. The creator must
 * not see who wrote (or didn't write) the charter. The brand-admin nudge
 * happens in the Brand Admin dashboard, not in the creator UI.
 *
 * Vitest negative test
 * `onboarding-default-charter-banner.test.tsx` asserts the rendered DOM
 * does not contain `props.brand.name` — regression canary against future
 * implementers adding `{{brand}}` interpolation.
 *
 * The i18n key `signup.charter.default_banner` value MUST also remain
 * free of `{{brand}}` placeholders. See `config/locales/frontend/onboarding.{en,fr}.yml`.
 */
export function OnboardingDefaultCharterBanner() {
  const { t } = useTranslation()
  return (
    <Alert
      // shadcn `Alert` ships role="alert" only on `variant="destructive"`.
      // Default variant carries no implicit role — we add a soft `role="note"`
      // so AT users get the explanatory context without an urgent
      // announcement (banner is informational, not actionable).
      role="note"
      data-testid="onboarding-charter-default-banner"
    >
      <Info className="size-4" aria-hidden="true" />
      <AlertDescription>{t("signup.charter.default_banner")}</AlertDescription>
    </Alert>
  )
}

export default OnboardingDefaultCharterBanner
